import { defaultTaskNotificationSubject, TaskTypeId } from "@sp-crm/core";
import {
    activityRecordTypeForEntityKind,
    recordTypeActivityChoices,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { Radio } from "components/ui/radio";
import { Select } from "components/ui/select";
import { WorkflowActionTaskEntity } from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import { produce } from "immer";
import React, { useCallback } from "react";
import { entityTypeNoun } from "./manage-workflows-helper";
import { RecurrenceEditor } from "./recurrence-editor";
import { EditableWorkflow } from "./types";

interface TaskActionEditorProps {
    workflow: EditableWorkflow;
    onChange: (workflow: EditableWorkflow) => void;
}

const TaskConfigurationEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, onChange } = props;

    const editable = !!workflow.regionVisibility?.editable;
    const taskNouns = useTaskNouns();

    const activityTypes = useActivityTypeOptions();

    const handleTypeChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newWorkflow = produce(workflow, draft => {
                const action = draft.actions[0] as WorkflowActionTaskEntity;

                if (action) {
                    action.taskTypeId = e.target.value || null;
                }
            });

            onChange(newWorkflow);
        },
        [workflow, onChange],
    );

    const action = workflow.actions[0] as WorkflowActionTaskEntity;

    const offeredTypes = recordTypeActivityChoices(
        activityTypes,
        activityRecordTypeForEntityKind(workflow.attachTo),
        (action.taskTypeId as TaskTypeId | null) ?? null,
    );

    return (
        <div className="space-y-4">
            <div>
                <Select
                    disabled={!editable}
                    label="Activity type"
                    value={action.taskTypeId || ""}
                    onChange={handleTypeChange}>
                    <option value=""></option>
                    {offeredTypes.map(t => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </Select>
                <div className="ml-6 text-gray-500 group-hover:text-gray-900 text-sm">
                    {`${taskNouns.titlePlural} created by this workflow will use this type.`}
                </div>
            </div>

            <RecurrenceEditor
                workflow={workflow}
                onChange={onChange}
                editable={editable}
            />
        </div>
    );
};

const TaskNotificationEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, onChange } = props;

    const editable = !!workflow.regionVisibility?.editable;
    const noun = entityTypeNoun(workflow.attachTo);
    const taskNouns = useTaskNouns();

    const action = workflow.actions[0] as WorkflowActionTaskEntity;

    const handleNotificationModeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const mode = e.target.value as "manual" | "automated";

            const newWorkflow = produce(workflow, draft => {
                const action = draft.actions[0] as WorkflowActionTaskEntity;

                if (action) {
                    action.notifyOwner = mode === "automated";
                }
            });

            onChange(newWorkflow);
        },
        [workflow, onChange],
    );

    return (
        <div className="space-y-3">
            <div>
                <h3 className="font-medium text-gray-900">
                    {`${taskNouns.titleSingular} assignment notification`}
                </h3>
                <p className="text-sm text-gray-500">
                    {`For ${taskNouns.singular} owners with notifications enabled, choose how to notify them when this workflow creates a ${taskNouns.singular}.`}
                </p>
            </div>

            <div className="space-y-2">
                <Radio
                    disabled={!editable}
                    value="manual"
                    checked={!action.notifyOwner}
                    onChange={handleNotificationModeChange}
                    label={
                        <div>
                            <div>
                                Notify when not {noun} owner:{" "}
                                <span className="italic">
                                    {`[Jane Doe] assigned a ${taskNouns.singular} to you`}
                                </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                                {`Acts like a manual ${taskNouns.singular} assignment from the ${noun} owner. Avoids redundant notifications when the ${taskNouns.singular} and ${noun} owner are the same.`}
                            </div>
                        </div>
                    }
                />
                <Radio
                    disabled={!editable}
                    value="automated"
                    checked={action.notifyOwner}
                    onChange={handleNotificationModeChange}
                    label={
                        <div>
                            <div>
                                Always notify with subject:{" "}
                                <span className="italic">
                                    {action.notification?.subject ||
                                        defaultTaskNotificationSubject(taskNouns)}
                                </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                                <p>
                                    {`Will be sent even if the ${taskNouns.singular} owner is the ${noun} owner. Best for critical or delayed ${taskNouns.plural} to act as an additional reminder.`}
                                </p>
                            </div>
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export const TaskActionEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, onChange } = props;

    return (
        <div className="space-y-4">
            <TaskConfigurationEditor workflow={workflow} onChange={onChange} />
            <TaskNotificationEditor workflow={workflow} onChange={onChange} />
        </div>
    );
};
